import React, { useEffect, useMemo, useRef, useState } from "react";
import "./_Otp.css";

type OtpProps = {
    length?: number;            // default 6
    loading?: boolean;          // show spinner / disable submit
    phoneHint?: string;         // e.g. "***-****-05"
    onSubmit: (code: string) => void;
    onResend?: () => void;
    subtitle?: string;          // overrides default Hebrew subtitle
    submitLabel?: string;       // overrides default "כניסה"
    resendLabel?: string;       // overrides default "לא קיבלתי קוד"
    error?: string | null;      // optional error text under the inputs
    autoFocus?: boolean;        // focus first input on mount
    countdownSeconds?: number;  // if provided, disables resend until it hits 0
    onSendEmail?: () => void;
    sendEmailLabel?: string; // default: "שלח לי במייל"
};

const Otp: React.FC<OtpProps> = ({
    length = 5,
    loading = false,
    phoneHint,
    onSubmit,
    onResend,
    subtitle,
    submitLabel = "כניסה",
    resendLabel = "לא קיבלתי קוד",
    error,
    autoFocus = true,
    countdownSeconds = 30,
    onSendEmail,
    sendEmailLabel = "שלח לי קוד במייל", // NEW
}) => {
    const [values, setValues] = useState<string[]>(Array(length).fill(""));
    const [focused, setFocused] = useState(0);
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const [timer, setTimer] = useState<number>(countdownSeconds);

    const code = useMemo(() => values.join(""), [values]);
    const canSubmit = code.length === length && values.every(v => v !== "") && !loading;

    useEffect(() => {
        if (!countdownSeconds) return;
        setTimer(countdownSeconds);
    }, [countdownSeconds]);

    useEffect(() => {
        if (!countdownSeconds) return;
        if (timer <= 0) return;
        const id = setInterval(() => setTimer(t => t - 1), 1000);
        return () => clearInterval(id);
    }, [timer, countdownSeconds]);

    useEffect(() => {
        if (autoFocus && inputsRef.current[0]) {
            inputsRef.current[0].focus();
            inputsRef.current[0] && inputsRef.current[0].select();
        }
    }, [autoFocus]);

    const setChar = (idx: number, ch: string) => {
        setValues(prev => {
            const next = [...prev];
            next[idx] = ch;
            return next;
        });
    };

    const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const onlyDigits = raw.replace(/\D/g, "");
        if (!onlyDigits) {
            setChar(idx, "");
            return;
        }

        const chars = onlyDigits.split("");
        setValues(prev => {
            const next = [...prev];
            let i = idx;
            for (const c of chars) {
                if (i >= length) break;
                next[i] = c;
                i++;
            }
            return next;
        });

        const nextIndex = Math.min(idx + chars.length, length - 1);
        requestAnimationFrame(() => {
            if (inputsRef.current[nextIndex]) {
                inputsRef.current[nextIndex]!.focus();
                inputsRef.current[nextIndex] && inputsRef.current[nextIndex]!.select();
            }
            setFocused(nextIndex);
        });
    };

    const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;

        if (key === "Backspace") {
            if (values[idx]) {
                setChar(idx, "");
                return;
            }
            if (idx > 0) {
                e.preventDefault();
                if (inputsRef.current[idx - 1]) inputsRef.current[idx - 1]!.focus();
                setChar(idx - 1, "");
                setFocused(idx - 1);
            }
            return;
        }

        if (key === "ArrowLeft") {
            e.preventDefault();
            const prev = Math.max(0, idx - 1);
            if (inputsRef.current[prev]) inputsRef.current[prev]!.focus();
            setFocused(prev);
            return;
        }

        if (key === "ArrowRight") {
            e.preventDefault();
            const next = Math.min(length - 1, idx + 1);
            if (inputsRef.current[next]) inputsRef.current[next]!.focus();
            setFocused(next);
            return;
        }

        if (key === "Home") {
            e.preventDefault();
            if (inputsRef.current[0]) inputsRef.current[0]!.focus();
            setFocused(0);
            return;
        }

        if (key === "End") {
            e.preventDefault();
            const last = length - 1;
            if (inputsRef.current[last]) inputsRef.current[last]!.focus();
            setFocused(last);
            return;
        }

        if (/^\d$/.test(key)) {
            e.preventDefault();
            setChar(idx, key);
            if (idx < length - 1) {
                const nxt = idx + 1;
                if (inputsRef.current[nxt]) inputsRef.current[nxt]!.focus();
                setFocused(nxt);
            }
            return;
        }
    };


    const handleFocus = (idx: number) => setFocused(idx);

    const submit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (canSubmit) onSubmit(code);
    };

    const resend = () => {
        if (!onResend) return;
        if (timer > 0) return;
        onResend();
        if (countdownSeconds) setTimer(countdownSeconds);
    };

    const emailClick = () => {
        if (!onSendEmail) return;
        if (loading) return;
        onSendEmail();
        if (timer > 0) return;
        if (countdownSeconds) setTimer(countdownSeconds);
    };

    const defaultSubtitle = `שלחנו לך קוד אימות בהודעת SMS${phoneHint ? ` ל-${phoneHint}` : ""}. מה הקוד שקיבלת?`;

    return (
        <div className="otp-root" dir="rtl">
            <div className="otp-card">
                <h1 className="otp-title">קוד אימות</h1>
                <p className="otp-subtitle">
                    {subtitle ? defaultSubtitle : ''}
                </p>

                <form onSubmit={submit}>
                    <div className="otp-inputs" aria-label="OTP">
                        {Array.from({ length }).map((_, i) => (
                            <React.Fragment key={i}>
                                <input
                                    ref={el => (inputsRef.current[i] = el)}
                                    className={`otp-input ${focused === i ? "is-focused" : ""}`}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={length} // allows multi-paste into one
                                    value={values[i]}
                                    onChange={e => handleChange(i, e)}
                                    onFocus={() => handleFocus(i)}
                                    onKeyDown={e => handleKeyDown(i, e)}
                                    aria-label={`ספרה ${i + 1}`}
                                />
                                {i < length - 1 && <span className="otp-sep">-</span>}
                            </React.Fragment>
                        ))}
                    </div>

                    {error ? <div className="otp-error">{error}</div> : null}

                    <div className="otp-actions">
                        <button
                            type="submit"
                            className={`otp-primary ${!canSubmit ? "is-disabled" : ""}`}
                            disabled={!canSubmit}
                        >
                            {loading ? "טוען…" : submitLabel}
                        </button>

                        {onResend && (
                            <button
                                type="button"
                                className="otp-secondary"
                                onClick={resend}
                                disabled={timer > 0}
                                aria-disabled={timer > 0}
                                title={timer > 0 ? `ניתן לשלוח שוב בעוד ${timer} שניות` : ""}
                            >
                                {resendLabel}{timer > 0 ? ` (${timer})` : ""}
                            </button>
                        )}
                    </div>
                    {onSendEmail && (
                        <div className="otp-under">
                            <button
                                type="button"
                                className="otp-link"
                                onClick={emailClick}
                                disabled={loading}
                            >
                                {sendEmailLabel}
                            </button>
                        </div>
                    )}
                </form>
            </div >
        </div >
    );
};

export default Otp;